import { useAuth0 } from '@auth0/auth0-react';
import React, { createContext, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getAuthUser, loginUser } from '../accessors';
import { getActiveHash, setContractStorage } from '../cookieManager';
import { useResetRecoilState } from 'recoil';
import { selectedContractState } from '../state/selectedContractState';
import { contractsState } from '../state/contractsState';
import { selectedPlanState } from '../state/selectedPlanState';
import { useYears } from '../hooks/useYears';
import { queryClient } from '../lib/react-query';
import { allowedRoutesState } from '../state/allowedRoutesState';
export const AuthContext = createContext({});

export function AuthProvider({ children }) {
  const { state } = useLocation();
  const { logout: logoutAuth0, user: auth0User, getAccessTokenSilently } = useAuth0();
  const [user, setUser] = useState();
  const [error, setError] = useState();
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const resetSelectedContract = useResetRecoilState(selectedContractState);
  const resetContractsState = useResetRecoilState(contractsState);
  const resetSelectedPlan = useResetRecoilState(selectedPlanState);
  const resetAllowedRoutes = useResetRecoilState(allowedRoutesState);
  const navigate = useNavigate();
  const { setStarsYear, DEFAULT_STARS_YEAR } = useYears();

  useEffect(() => {
    //page refresh and after auth0 login
    let activeHash = getActiveHash();
    const getAuth0Token = async () => {
      const accessToken = await getAccessTokenSilently();
      return accessToken;
    };
    if (auth0User) {
      localStorage.setItem('authorization_method', 'sso');
    }
    if (localStorage.getItem('authorization_method') === 'sso' && auth0User && !user) {
      setIsLoading(true);
      getAuth0Token().then((token) => {
        fetchUserDetails(token);
      });
      return;
    } else if (activeHash && !user) {
      fetchUserDetails(activeHash);
      return;
    } else if (auth0User && !user) {
      setIsLoading(true);
      getAuth0Token().then((token) => {
        fetchUserDetails(token);
      });
    } else {
      setIsLoading(false);
    }
  }, [auth0User, getAccessTokenSilently]);

  function handleNoAccount(res) {
    setIsLoading(false);
    localStorage.clear();
    navigate('/noAccount');
  }

  async function fetchUserDetails(token) {
    setIsLoading(true);
    getAuthUser(token)
      .then((res) => {
        if (!res?.id) {
          handleNoAccount(res);
          return;
        }
        if (!res?.products?.find((p) => p.name === process.env.REACT_APP_PRODUCT_NAME)) {
          handleNoAccount(res);
          return;
        }
        const response = { ...res };
        //res.data.forEach((d) => (details[d.field] = d.value));
        response.active_hash = token;
        response.userid = response.id;
        response.user_id = response.id;
        localStorage.setItem('active_hash', token);
        setUser(response);
        setIsLoading(false);
      })
      .catch((error) => {
        setIsLoading(false);
        logout();
      });
  }

  async function login(usernameAndPassword) {
    setIsSubmitting(true);
    setError(false);
    const response = await loginUser(usernameAndPassword);
    if (response.ok) {
      const jsonResponse = await response.json();
      setStarsYear(DEFAULT_STARS_YEAR);
      localStorage.setItem('active_hash', jsonResponse.token);
      localStorage.setItem('tour_complete', jsonResponse.user.tour_complete);
      //const user = { ...jsonResponse.user };
      //different userid vs user_id on getUserDetails
      //user.user_id = user.id;
      //user.userid = user.id;
      //setUser(user);
      await fetchUserDetails(jsonResponse.token);
      setContractStorage('');
      localStorage.removeItem('peerAnalysisModel');
      setIsSubmitting(false);
      navigate(state?.path || '/directory');
      return;
    }
    if (response.status === 401) {
      setError('Incorrect username or password');
      setIsSubmitting(false);
    } else {
      setError('Login failed. Please try again');
      setIsSubmitting(false);
    }
  }

  function logout(e) {
    e?.preventDefault();
    //var menuGroupPersist = localStorage.getItem('menuGroup');
    localStorage.clear();
    queryClient.clear();
    //nuGroup', menuGroupPersist);
    resetSelectedContract();
    resetContractsState();
    resetSelectedPlan();
    resetAllowedRoutes();
    setUser(null);
    if (auth0User) {
      logoutAuth0({ logoutParams: { returnTo: window.location.origin + '/signin' } });
    }
  }

  const isLoggedIn = user?.active_hash;

  let value = {
    user,
    isLoading,
    isSubmitting,
    error,
    login,
    logout,
    isLoggedIn,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
